import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, CardFooter, Input, Textarea, Switch } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { useSupplier, useSupplierMutations, type Supplier } from '@/api/capacities';

interface FormState {
  code: string;
  name: string;
  address: string;
  is_active: boolean;
}

const EMPTY: FormState = { code: '', name: '', address: '', is_active: true };

const toForm = (s: Supplier): FormState => ({
  code: s.code ?? '',
  name: s.name ?? '',
  address: s.address ?? '',
  is_active: s.is_active ?? true,
});

/**
 * Supplier create / edit form (`/capacities/suppliers/new` and `/:id/edit`).
 * Fields: code · name · address · active. On success navigates to the supplier
 * detail (edit) or the list (create). Wired to `useSupplierMutations` for real
 * create/update — live execution is performed by the user.
 */
export default function CapacitySupplierFormPage() {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id ?? '';
  const isEdit = !!id;

  const { data: supplier, isLoading } = useSupplier(id);
  const { create, update } = useSupplierMutations();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  // Validate-on-touch: surface "Required" only after blur or a submit attempt.
  const [touched, setTouched] = useState({ code: false, name: false });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (supplier) setForm(toForm(supplier));
  }, [supplier]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const touch = (key: keyof typeof touched) => setTouched((t) => ({ ...t, [key]: true }));

  const codeInvalid = form.code.trim() === '';
  const nameInvalid = form.name.trim() === '';
  const invalid = codeInvalid || nameInvalid;
  const showCode = (touched.code || submitted) && codeInvalid;
  const showName = (touched.name || submitted) && nameInvalid;
  const submitting = create.isPending || update.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (invalid) {
      setError('Code and name are required.');
      return;
    }
    setError(null);
    const body: Partial<Supplier> = {
      code: form.code.trim(),
      name: form.name.trim(),
      address: form.address.trim() || undefined,
      is_active: form.is_active,
    };
    const opts = {
      onSuccess: () => navigate(isEdit ? `/capacities/suppliers/${id}` : '/capacities/suppliers'),
      onError: () => setError('Failed to save the supplier. Please try again.'),
    };
    if (isEdit) update.mutate({ id, body }, opts);
    else create.mutate(body, opts);
  };

  const cancelTo = isEdit ? `/capacities/suppliers/${id}` : '/capacities/suppliers';

  return (
    <Container size="full">
      <div style={{ marginBottom: 'var(--vn-space-4)' }}>
        <Button asChild variant="ghost" size="sm">
          <button type="button" onClick={() => navigate(cancelTo)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              <ArrowLeft size={16} />
              Cancel
            </span>
          </button>
        </Button>
      </div>

      <PageHeader
        title={isEdit ? 'Edit supplier' : 'New supplier'}
        description="Suppliers provide equipment and components for the warehouse."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      {isEdit && isLoading ? (
        <TabLoading rows={4} />
      ) : (
        <form onSubmit={handleSubmit}>
          <Card flat>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                {error ? <Alert tone="danger" title={error} /> : null}
                <Field label="Code" required error={showCode ? 'Required' : undefined}>
                  {(p) => (
                    <Input
                      {...p}
                      value={form.code}
                      invalid={showCode}
                      onBlur={() => touch('code')}
                      onChange={(e) => set('code', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Name" required error={showName ? 'Required' : undefined}>
                  {(p) => (
                    <Input
                      {...p}
                      value={form.name}
                      invalid={showName}
                      onBlur={() => touch('name')}
                      onChange={(e) => set('name', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Address">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={form.address}
                      onChange={(e) => set('address', e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Active" description="Inactive suppliers are hidden from item pickers.">
                  {() => (
                    <Switch
                      checked={form.is_active}
                      onCheckedChange={(v) => set('is_active', v)}
                    />
                  )}
                </Field>
              </div>
            </CardBody>
            <CardFooter style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
              <Button type="button" variant="ghost" onClick={() => navigate(cancelTo)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
                {isEdit ? 'Save changes' : 'Create supplier'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      )}
    </Container>
  );
}
